import { useMutation, useQueryClient } from "@tanstack/react-query";
import { z } from "zod";

import { useShipmentNotifications } from "~/features/shipment-management/hooks/use-shipment-notifications.ts";
import { apiRoutes } from "~/shared/services/api/api-routes.ts";
import { postAndValidate } from "~/shared/services/api/fetch-client.ts";

export const postBillHoldValidation = async (
	poNumber: number,
	customerId: number,
	reason = "string",
	estimatedDate = new Date().toISOString(),
) => {
	const params = new URLSearchParams({
		poNumber: poNumber.toString(),
		customerId: customerId.toString(),
	});
	const url = `${apiRoutes.POSTBILLHOLDVALIDATION}?${params.toString()}`;
	const resp = await postAndValidate(
		url,
		{
			reason,
			estimatedDate,
		},
		z.unknown(),
	);

	if (resp.success) {
		return resp.data;
	}

	throw new Error(resp.error);
};

export const postBillHold = async (
	poNumber: number,
	reason = "string",
	estimatedDate = new Date().toISOString(),
) => {
	const params = new URLSearchParams({ poNumber: poNumber.toString() });
	const url = `${apiRoutes.POSTBILLHOLD}?${params.toString()}`;
	const resp = await postAndValidate(
		url,
		{
			reason,
			estimatedDate,
		},
		z.unknown(),
	);

	if (resp.success) {
		return resp.data;
	}

	throw new Error(resp.error);
};

export const usePostBillHold = () => {
	const queryClient = useQueryClient();
	const { showSuccess, showError } = useShipmentNotifications();
	const { mutate, mutateAsync, isPending, error, data, reset } = useMutation<
		unknown,
		Error,
		{
			poNumber: number;
			customerId: number;
			reason?: string;
			estimatedDate?: string;
		}
	>({
		mutationFn: async ({
			poNumber,
			customerId,
			reason,
			estimatedDate,
		}: {
			poNumber: number;
			customerId: number;
			reason?: string;
			estimatedDate?: string;
		}) => {
			await postBillHoldValidation(poNumber, customerId, reason, estimatedDate);
			return postBillHold(poNumber, reason, estimatedDate);
		},
		onSuccess: () => {
			void queryClient.invalidateQueries({ queryKey: ["shipment"] });
			showSuccess("Bill hold saved.");
		},
		onError: (err) => {
			showError(
				err instanceof Error
					? err.message
					: "Failed to save bill hold. Please try again.",
			);
		},
	});

	return {
		billHold: mutate,
		billHoldAsync: mutateAsync,
		isSavingBillHold: isPending,
		error,
		data,
		resetError: reset,
	};
};
